import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Global Button Component
 * 
 * Touch-friendly buttons with 44px minimum tap target (industry standard)
 * Pattern: GitHub, Linear, Stripe, Vercel
 * 
 * Features:
 * - Multiple variants (primary, secondary, danger, ghost, outline)
 * - Size variants with mobile-first approach
 * - Loading states with spinner
 * - Disabled states
 * - Icon support (left/right positioning)
 * - Full-width option
 * - Keyboard accessible
 * 
 * Size Guidelines (WCAG 2.5.5 - Target Size):
 * - Mobile: 44px minimum (easier tapping)
 * - Desktop: Can be smaller (precise mouse)
 * 
 * @example
 * ```tsx
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Save Changes
 * </Button>
 * 
 * <Button 
 *   variant="primary" 
 *   loading 
 *   icon={<Save className="w-4 h-4" />}
 *   iconPosition="left"
 * >
 *   Saving...
 * </Button>
 * ```
 */

export interface ButtonProps {
  /** Visual style variant */
  variant: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline';
  
  /** Size with mobile-first approach */
  size?: 'sm' | 'md' | 'lg';
  
  /** Take full width of container */
  fullWidth?: boolean;
  
  /** Show loading spinner */
  loading?: boolean;
  
  /** Disable button */
  disabled?: boolean;
  
  /** Optional icon */
  icon?: React.ReactNode;
  
  /** Icon position */
  iconPosition?: 'left' | 'right';
  
  /** Click handler */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  
  /** Button content */
  children: React.ReactNode;
  
  /** Button type */
  type?: 'button' | 'submit' | 'reset';
  
  /** Additional CSS classes */
  className?: string;
  
  /** Accessibility label */
  ariaLabel?: string;
}

/**
 * Variant style mapping
 * Following industry color conventions
 */
const variantStyles = {
  primary: `
    bg-blue-600 dark:bg-blue-700 
    text-white 
    border-2 border-blue-600 dark:border-blue-700
    hover:bg-blue-700 dark:hover:bg-blue-600 
    hover:border-blue-700 dark:hover:border-blue-600
    active:bg-blue-800 dark:active:bg-blue-500
    disabled:bg-blue-300 dark:disabled:bg-blue-800
    disabled:border-blue-300 dark:disabled:border-blue-800
    shadow-sm hover:shadow-md
  `,
  secondary: `
    bg-gray-100 dark:bg-slate-700 
    text-gray-900 dark:text-slate-100 
    border-2 border-gray-300 dark:border-slate-600
    hover:bg-gray-200 dark:hover:bg-slate-600 
    hover:border-gray-400 dark:hover:border-slate-500
    active:bg-gray-300 dark:active:bg-slate-500
    disabled:bg-gray-50 dark:disabled:bg-slate-800
    disabled:border-gray-200 dark:disabled:border-slate-700
  `,
  danger: `
    bg-red-600 dark:bg-red-700 
    text-white 
    border-2 border-red-600 dark:border-red-700
    hover:bg-red-700 dark:hover:bg-red-600 
    hover:border-red-700 dark:hover:border-red-600
    active:bg-red-800 dark:active:bg-red-500
    disabled:bg-red-300 dark:disabled:bg-red-800
    disabled:border-red-300 dark:disabled:border-red-800
    shadow-sm hover:shadow-md
  `,
  ghost: `
    bg-transparent 
    text-gray-700 dark:text-slate-300 
    border-2 border-transparent
    hover:bg-gray-100 dark:hover:bg-slate-700 
    active:bg-gray-200 dark:active:bg-slate-600
    disabled:text-gray-400 dark:disabled:text-slate-600
  `,
  outline: `
    bg-transparent 
    text-blue-600 dark:text-blue-400 
    border-2 border-blue-600 dark:border-blue-500
    hover:bg-blue-50 dark:hover:bg-blue-900/20 
    hover:border-blue-700 dark:hover:border-blue-400
    active:bg-blue-100 dark:active:bg-blue-900/40
    disabled:text-blue-300 dark:disabled:text-blue-700
    disabled:border-blue-300 dark:disabled:border-blue-700
  `
};

/**
 * Size mapping with mobile-first approach
 * Mobile: Larger touch targets (44px min)
 * Desktop: Can be more compact
 */
const sizeStyles = {
  sm: 'min-h-[40px] lg:min-h-[36px] px-3 lg:px-3 text-sm',
  md: 'min-h-[48px] lg:min-h-[40px] px-4 lg:px-4 text-sm lg:text-base',
  lg: 'min-h-[56px] lg:min-h-[48px] px-6 lg:px-6 text-base lg:text-lg'
};

export const Button: React.FC<ButtonProps> = ({
  variant,
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled = false,
  icon,
  iconPosition = 'left',
  onClick,
  children,
  type = 'button',
  className = '',
  ariaLabel
}) => {
  const isDisabled = disabled || loading;

  // Build class string
  const variantClass = variantStyles[variant].trim().replace(/\s+/g, ' ');
  const sizeClass = sizeStyles[size];
  const widthClass = fullWidth ? 'w-full' : '';

  const baseClasses = `
    inline-flex items-center justify-center
    font-semibold
    rounded-lg
    transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
    disabled:cursor-not-allowed disabled:opacity-50
    transform active:scale-[0.98]
  `.trim().replace(/\s+/g, ' ');

  const combinedClassName = [
    baseClasses,
    variantClass,
    sizeClass,
    widthClass,
    className
  ].filter(Boolean).join(' ');

  // Handle icon sizing based on button size
  const iconSize = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
  const iconClass = iconPosition === 'left' ? 'mr-2' : 'ml-2';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={combinedClassName}
      aria-label={ariaLabel}
      aria-busy={loading}
    >
      {/* Loading spinner (left side) */}
      {loading && iconPosition === 'left' && (
        <Loader2 className={`${iconSize} ${iconClass} animate-spin`} />
      )}

      {/* Icon (left side) */}
      {!loading && icon && iconPosition === 'left' && (
        <span className={iconClass}>{icon}</span>
      )}

      {/* Button content */}
      <span>{children}</span>

      {/* Icon (right side) */}
      {!loading && icon && iconPosition === 'right' && (
        <span className={iconClass}>{icon}</span>
      )}

      {/* Loading spinner (right side) */}
      {loading && iconPosition === 'right' && (
        <Loader2 className={`${iconSize} ${iconClass} animate-spin`} />
      )}
    </button>
  );
};

/**
 * Convenience components for common button types
 */

/** Primary action button */
export const PrimaryButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="primary" {...props} />
);

/** Secondary action button */
export const SecondaryButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="secondary" {...props} />
);

/** Danger/destructive action button */
export const DangerButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="danger" {...props} />
);

/** Ghost button (minimal style) */
export const GhostButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="ghost" {...props} />
);

/** Outline button */
export const OutlineButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="outline" {...props} />
);

